import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import { useGetShippingsQuery } from '../services/shippings.service';
import { Option } from '@/components/inputs/AutocompleteInput';
import type { AddressApi } from '../models';

interface UseOriginAddressAutocompleteProps {
  search?: string;
  minLength?: number;
  enabled?: boolean;
}

function originAddressLine(
  address: AddressApi | null | undefined
): string | null {
  if (!address) return null;
  const parts = [
    address.streetName,
    address.doorNumber,
    address.apartmentNumber,
  ].filter(Boolean);
  const line = parts.join(' ').trim();
  return line.length > 0 ? line : null;
}

const useOriginAddressAutocomplete = ({
  search,
  minLength = 3,
  enabled = true,
}: UseOriginAddressAutocompleteProps = {}) => {
  const {
    debouncedSearch: originAddressSearchTextTrimmed,
    shouldSearch: debouncedShouldFetchOriginAddressSuggestions,
    isDebouncePending,
  } = useAutocompleteSearch(search, minLength);
  const shouldFetchOriginAddressSuggestions = enabled && debouncedShouldFetchOriginAddressSuggestions;

  const {
    data: shippingsResponse,
    isFetching: isOriginAddressFetching,
    error: originAddressError,
  } = useGetShippingsQuery(
    {
      paginated: { page: 1, pageSize: 10 },
      filters: {
        originAddress: shouldFetchOriginAddressSuggestions
          ? originAddressSearchTextTrimmed
          : '',
      },
    },
    { skip: !shouldFetchOriginAddressSuggestions }
  );

  const resolvedOriginAddressOptions: Option[] = useMemo(() => {
    if (!shippingsResponse?.items) return [];

    const addressLineByCaseInsensitiveKey = new Map<string, string>();

    for (const shipping of shippingsResponse.items) {
      const addressLine = originAddressLine(shipping.originAddress);
      if (!addressLine) continue;

      const caseInsensitiveAddressKey = addressLine.toLocaleLowerCase('es');

      if (!addressLineByCaseInsensitiveKey.has(caseInsensitiveAddressKey)) {
        addressLineByCaseInsensitiveKey.set(
          caseInsensitiveAddressKey,
          addressLine
        );
      }
    }

    const uniqueAddressLines = [...addressLineByCaseInsensitiveKey.values()];

    uniqueAddressLines.sort(
      (firstComparedAddressLine, secondComparedAddressLine) =>
        firstComparedAddressLine.localeCompare(
          secondComparedAddressLine,
          'es',
          { sensitivity: 'base' }
        )
    );

    return uniqueAddressLines.map((addressLine) => ({
      label: addressLine,
      data: addressLine,
    }));
  }, [shippingsResponse?.items]);

  const originAddressOptions = useStableOptions(resolvedOriginAddressOptions, {
    isPending: isOriginAddressFetching || isDebouncePending,
    enabled: shouldFetchOriginAddressSuggestions || (enabled && isDebouncePending),
  });

  const isOriginAddressLoading = shouldFetchOriginAddressSuggestions && isOriginAddressFetching;

  return {
    originAddressOptions,
    isOriginAddressLoading,
    originAddressError,
  };
};

export default useOriginAddressAutocomplete;
